﻿import VideoPlayer from "@/pages/video/components/VideoPlayer";
import { juliangApi, type JuliangVideo } from "@/services/juliangApi";
import { library } from "@/services/library";
import { analytics } from "@/services/analytics";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const formatViews = (value: number) =>
  value >= 100000
    ? `${(value / 10000).toFixed(2).replace(/\.00$/, "")}万`
    : value >= 10000
      ? `${(value / 10000).toFixed(1).replace(/\.0$/, "")}万`
      : value >= 1000
        ? `${(value / 1000).toFixed(1).replace(/\.0$/, "")}千`
        : String(value);
const queryNumber = (name: string) =>
  Math.max(0, Number(new URLSearchParams(location.search).get(name)) || 0);

export default function CinemaDetail() {
  const { id = "" } = useParams(),
    navigate = useNavigate(),
    [video, setVideo] = useState<JuliangVideo>(),
    [sourceIndex, setSourceIndex] = useState(() => queryNumber("source")),
    [episodeIndex, setEpisodeIndex] = useState(() => queryNumber("episode")),
    [duration, setDuration] = useState(0),
    [error, setError] = useState("");
  const latestProgress = useRef(0),
    lastSaved = useRef(0),
    started = useRef(false),
    durationRef = useRef(0),
    videoRef = useRef<JuliangVideo>(),
    selectionRef = useRef({ sourceIndex, episodeIndex });
  const playTracked = useRef(false),
    effectiveTracked = useRef(false),
    completedTracked = useRef(false),
    startupTracked = useRef(false),
    startupAt = useRef(0),
    watchSeconds = useRef(0),
    totalWatchSeconds = useRef(0);
  selectionRef.current = { sourceIndex, episodeIndex };
  const source = video?.sources[sourceIndex],
    episode = source?.episodes[episodeIndex];
  const historyId = video
    ? `juliang:${video.id}:${sourceIndex}:${episodeIndex}`
    : "";
  const initialTime = historyId ? library.progress(historyId) : 0;
  const eventMetadata = () => ({
    library: "juliang",
    title: (videoRef.current?.title || "免费影院视频").slice(0, 200),
    source: String(selectionRef.current.sourceIndex),
    episode: String(selectionRef.current.episodeIndex),
  });
  const flushAnalytics = () => {
    if (historyId && watchSeconds.current >= 1) {
      analytics.track("watch_time", {
        videoId: historyId,
        value: Math.floor(watchSeconds.current),
        metadata: eventMetadata(),
      });
      watchSeconds.current = 0;
      analytics.flush();
    }
  };
  const m3u8SourceIndex = useMemo(
    () =>
      video?.sources.findIndex((item) =>
        item.episodes.some((ep) => /\.m3u8(?:\?|$)/i.test(ep.url)),
      ) ?? -1,
    [video],
  );
  const record = (completed = false) => {
    const current = videoRef.current,
      selection = selectionRef.current,
      currentSource = current?.sources[selection.sourceIndex],
      currentEpisode = currentSource?.episodes[selection.episodeIndex];
    if (!current || !currentEpisode || !started.current) return;
    library.recordJuliang(
      current,
      selection.sourceIndex,
      selection.episodeIndex,
      currentEpisode.name,
      latestProgress.current,
      durationRef.current,
      completed,
    );
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    void juliangApi
      .detail(id)
      .then((value) => {
        setVideo(value);
        videoRef.current = value;
        document.title = value.title;
        const requestedSource = queryNumber("source");
        if (
          !new URLSearchParams(location.search).has("source") &&
          value.sources.findIndex((item) =>
            item.episodes.some((ep) => /\.m3u8(?:\?|$)/i.test(ep.url)),
          ) >= 0
        )
          setSourceIndex(
            value.sources.findIndex((item) =>
              item.episodes.some((ep) => /\.m3u8(?:\?|$)/i.test(ep.url)),
            ),
          );
        else
          setSourceIndex(
            Math.min(requestedSource, Math.max(0, value.sources.length - 1)),
          );
      })
      .catch((e) => setError((e as Error).message));
    const flush = () => {
      record(false);
      flushAnalytics();
    };
    window.addEventListener("pagehide", flush);
    return () => {
      flush();
      window.removeEventListener("pagehide", flush);
    };
  }, [id]);
  useEffect(() => {
    if (
      video &&
      m3u8SourceIndex >= 0 &&
      sourceIndex === 0 &&
      !new URLSearchParams(location.search).has("source")
    )
      setSourceIndex(m3u8SourceIndex);
  }, [video, m3u8SourceIndex]);
  useEffect(() => {
    flushAnalytics();
    latestProgress.current = initialTime;
    lastSaved.current = initialTime;
    started.current = false;
    durationRef.current = 0;
    playTracked.current = false;
    effectiveTracked.current = false;
    completedTracked.current = false;
    startupTracked.current = false;
    startupAt.current = 0;
    watchSeconds.current = 0;
    totalWatchSeconds.current = 0;
    setDuration(0);
    const url = new URL(location.href);
    url.searchParams.set("source", String(sourceIndex));
    url.searchParams.set("episode", String(episodeIndex));
    history.replaceState(null, "", `${url.pathname}${url.search}`);
    if (video && historyId)
      analytics.track("video_detail_view", {
        videoId: historyId,
        metadata: eventMetadata(),
      });
  }, [sourceIndex, episodeIndex, historyId]);
  const selectSource = (index: number) => {
    record(false);
    setSourceIndex(index);
    setEpisodeIndex(0);
  };
  const selectEpisode = (index: number) => {
    record(false);
    setEpisodeIndex(index);
  };
  if (error)
    return (
      <main className="grid min-h-screen place-items-center bg-black p-6 text-white">
        <div>
          {error}
          <button
            onClick={() => navigate(-1)}
            className="ml-3 text-primary-400"
          >
            返回
          </button>
        </div>
      </main>
    );
  if (!video) return <main className="min-h-screen animate-pulse bg-black" />;
  return (
    <div className="mx-auto min-h-screen w-full max-w-[760px] bg-background-50 pb-10">
      <header className="app-safe-header flex h-auto min-h-[calc(56px+var(--safe-top))] items-end gap-2 bg-black pb-2 text-white">
        <button
          onClick={() => navigate(-1)}
          aria-label="返回"
          className="grid h-11 w-11 flex-none place-items-center rounded-full active:bg-white/15"
        >
          <i className="ri-arrow-left-line text-2xl" />
        </button>
        <b className="mb-3 min-w-0 flex-1 truncate text-sm">{video.title}</b>
      </header>
      {episode ? (
        <VideoPlayer
          key={episode.url}
          src={episode.url}
          poster={video.backdrop || video.thumbnail}
          title={video.title}
          trimStart={0}
          trimEnd={0}
          initialTime={initialTime}
          onPlay={() => {
            started.current = true;
            record(false);
            if (!startupAt.current) startupAt.current = performance.now();
            if (!playTracked.current) {
              playTracked.current = true;
              analytics.track("video_play", {
                videoId: historyId,
                metadata: eventMetadata(),
              });
            }
          }}
          onPlaying={() => {
            if (!startupTracked.current && startupAt.current) {
              startupTracked.current = true;
              analytics.track("playback_startup", {
                videoId: historyId,
                value: Math.round(performance.now() - startupAt.current),
                metadata: {
                  ...eventMetadata(),
                  measurement: "play_to_playing_v2",
                },
              });
            }
          }}
          onDuration={(seconds) => {
            durationRef.current = seconds;
            setDuration(seconds);
          }}
          onProgress={(time) => {
            latestProgress.current = time;
            if (
              started.current &&
              (time - lastSaved.current >= 5 || time < lastSaved.current)
            ) {
              lastSaved.current = time;
              record(false);
            }
          }}
          onWatch={(seconds) => {
            watchSeconds.current += seconds;
            totalWatchSeconds.current += seconds;
            if (!effectiveTracked.current && totalWatchSeconds.current >= 10) {
              effectiveTracked.current = true;
              analytics.track("video_effective", {
                videoId: historyId,
                metadata: {
                  ...eventMetadata(),
                  measurement: "watch_seconds_v2",
                },
              });
            }
            if (watchSeconds.current >= 15) flushAnalytics();
          }}
          onError={(detail) =>
            analytics.track("playback_error", {
              videoId: historyId,
              value: startupAt.current
                ? Math.round(performance.now() - startupAt.current)
                : 0,
              metadata: { ...eventMetadata(), ...detail },
            })
          }
          onEnded={() => {
            latestProgress.current = durationRef.current;
            record(true);
            flushAnalytics();
            if (!completedTracked.current) {
              completedTracked.current = true;
              analytics.track("video_complete", {
                videoId: historyId,
                metadata: eventMetadata(),
              });
            }
          }}
        />
      ) : (
        <div className="grid aspect-video place-items-center bg-black text-sm text-white/70">
          暂无可用播放线路
        </div>
      )}
      <main className="space-y-4 p-4">
        <div>
          <h1 className="text-lg font-bold text-foreground-900">
            {video.title}
          </h1>
          <p className="mt-1 text-xs text-foreground-500">
            {video.parentCategoryName} · {video.categoryName} · {video.remarks}
          </p>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span className="font-bold text-amber-500">
              {video.score > 0 ? `${video.score.toFixed(1)} 分` : "暂无评分"}
            </span>
            <span>
              <i className="ri-play-circle-line mr-1" />
              {formatViews(video.views)} 次播放
            </span>
            {duration > 0 && initialTime > 0 && (
              <span>续播 {Math.floor(initialTime / 60)} 分钟</span>
            )}
          </div>
        </div>
        {video.sources.length > 1 && (
          <section>
            <b className="mb-2 block text-sm">播放线路</b>
            <div className="flex flex-wrap gap-2">
              {video.sources.map((item, index) => (
                <button
                  key={`${item.name}-${index}`}
                  onClick={() => selectSource(index)}
                  className={`rounded-full px-3 py-1.5 text-xs ${index === sourceIndex ? "bg-primary-500 text-white" : "bg-background-100"}`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </section>
        )}
        {source && source.episodes.length > 1 && (
          <section>
            <b className="mb-2 block text-sm">选集</b>
            <div className="grid grid-cols-4 gap-2">
              {source.episodes.map((item, index) => (
                <button
                  key={`${item.name}-${index}`}
                  onClick={() => selectEpisode(index)}
                  className={`truncate rounded-lg px-2 py-2 text-xs ${index === episodeIndex ? "bg-primary-500 text-white" : "bg-background-100"}`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </section>
        )}
        <p className="text-sm leading-7 text-foreground-600">
          {video.description || "暂无简介"}
        </p>
      </main>
    </div>
  );
}
